import { makeStyles, Typography, Grid, useClientOptions, InputLabel, useDialogs } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useQuote } from '@paintscout/react-quote'
import type { QuoteTemplateOption } from '@paintscout/util/builder'
import {
  getQuoteTemplateOptions,
  updateQuoteTemplateOption,
  getObjectLabels,
  getFeature,
  getQuoteTypeOption
} from '@paintscout/util/builder'
import find from 'lodash/find'
import React, { useCallback, useState } from 'react'
import { joinWithOr } from '@paintscout/util/util'
import { useFlags } from 'shared/components/common/FlagsProvider'
import QuoteTemplateTitleDialog from './QuoteTemplateTitleDialog'

export interface QuoteTemplateTitleState {
  item?: QuoteTemplateOption
  initialItem?: QuoteTemplateOption
  editing?: boolean
}

const useStyles = makeStyles<Theme, QuoteTemplateTitleProps>(
  (theme) => ({
    root: {
      marginBottom: theme.typography.pxToRem(10)
    },
    title: (props) => {
      return {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        padding: '0.5px',
        marginTop: props.sticky ? theme.typography.pxToRem(0) : theme.typography.pxToRem(10)
      }
    },
    pageTitleWrapper: {
      margin: theme.typography.pxToRem(10),
      display: 'flex',
      flexDirection: 'column',
      gridGap: theme.spacing()
    }
  }),
  { name: 'QuoteTemplateTitle' }
)

export interface QuoteTemplateTitleProps {
  classes?: StyleClasses<typeof useStyles>
  sticky?: boolean
}

function QuoteTemplateTitle(props: QuoteTemplateTitleProps) {
  const classes = useStyles(props)
  const { options, updateOptions } = useClientOptions()
  const { quote } = useQuote()
  const { canDeactivateSettingsItems } = useFlags()
  const quoteTemplateOptions = getQuoteTemplateOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const { openDialog, dismissDialog } = useDialogs()
  const [item, setItem] = useState(find(quoteTemplateOptions, { key: quote._id }))
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const multiQuoteTemplates = getFeature({ options, path: 'quotes.multiQuoteTemplates' })

  function getQuoteTemplateTitle() {
    const quoteTypes = (item?.quoteTypes ?? [])
      .map((quoteType) => {
        const option = getQuoteTypeOption({ options, quoteType })
        return option?.label
      })
      .filter(Boolean)
    return `${joinWithOr(quoteTypes)} ${objectLabels.quote.value}`
  }

  const updateItem = useCallback(
    (item: QuoteTemplateOption) => {
      setItem(item)
      const updatedOptions = updateQuoteTemplateOption({ options, quoteTemplateOption: item })
      updateOptions({ options: updatedOptions })
    },
    [updateOptions]
  )

  function handleTitleClick(ev: React.MouseEvent) {
    ev.preventDefault()

    // @ts-ignore
    ev.currentTarget?.blur() // clear focus outline after click

    openDialog(QuoteTemplateTitleDialog, {
      onConfirm: async (newLabel: string, newTypes: string[]) => {
        updateItem({
          ...item,
          label: newLabel,
          quoteTypes: newTypes
        })
        dismissDialog()
      },
      onCancel: dismissDialog,
      item,
      setItem,
      quote,
      options
    })
  }

  return (
    <Grid container={true} spacing={1} className={classes.root}>
      <Grid item={true} xs={12} md={12}>
        <div className={classes.title} onClick={handleTitleClick}>
          <InputLabel shrink={true} variant={props?.sticky ? 'sublabel' : 'label'}>
            Template Label
          </InputLabel>
          <Typography variant="h1" component="h1" className={classes.quoteTitle} noWrap>
            {item?.label}
          </Typography>
          {item?.smartTemplate && multiQuoteTemplates && (
            <Typography variant="subtitle2">{'Viewing Template as:'}</Typography>
          )}
          <Typography variant="subtitle1">{getQuoteTemplateTitle()}</Typography>
        </div>
      </Grid>
    </Grid>
  )
}

export default QuoteTemplateTitle
